// Module 4.1 — Kill Sheet storage.
//
// Storage key: rsc.killsheets.v1
// Scoped per wellId.
// On every create / update, the outputs block is freshly re-computed and the
// frozen audit snapshot stored alongside.
//
// Locking (Refinement 2): once `status === "locked"`, the storage layer
// rejects any field mutation other than `status` transitions (locked →
// superseded / archived). Revisions live in 4.1b.

import { emptyKillSheet, validateKillSheet, KILLSHEET_STATUSES, KILLSHEET_SCHEMA_VERSION } from "./schema";
import { computeKillSheetOutputs } from "./compute";
import { stampEngineeringMetadata } from "@/lib/rce/snapshot";
import { KILLSHEET_CONSUMES, KILLSHEET_SNAPSHOT_TO_INPUT } from "./consumes";
import { scoped } from "@/lib/accountScope";

export const KILLSHEET_STORAGE_KEY = "rsc.killsheets.v1";

// ---------------------------------------------------------------------------
// RCE-4 — Resolver bootstrap (dependency injection — same pattern as
// `lib/mud/dmr.js`). Wired by `lib/rce/bootstrap.js`.
// ---------------------------------------------------------------------------
let _resolveFn = () => ({ value: "", confidence: "unknown", source: null });
export function _setResolver(fn) { if (typeof fn === "function") _resolveFn = fn; }

// The set of RCE field paths a kill sheet consumes — lives in
// `./consumes.js` so the Inheritance Audit can import it without pulling
// the full storage layer.

function _assignDottedPath(target, dotted, value) {
  const parts = dotted.split(".");
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!cur[p] || typeof cur[p] !== "object") cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

/** Build a fresh RCE-1.0 contextSnapshot for a kill sheet. Pure — does
 *  not touch storage. */
export function buildKillSheetContextSnapshot(ctx) {
  const snap = {
    frozen: true,
    snapshotId: "",
    contextVersion: "RCE-1.0",
    capturedAt: null,
    project: { operator: "", client: "", country: "", field: "" },
    rig:     { name: "", contractor: "", pumpOutputBblStk: "" },
    well:    {
      name: "", environment: "", waterDepthFt: "",
      holeSection: "", holeSizeIn: "",
      currentMdFt: "", currentTvdFt: "",
      casingShoeMdFt: "", casingShoeTvdFt: "",
      stringInternalVolBbl: "", bitToShoeVolBbl: "", shoeToSurfaceVolBbl: "",
      lotPressurePsi: "", mwAtLotPpg: "",
    },
    bha: { id: "", revision: "" },
    drillString: { id: "", revision: "" },
    mud: { weightInPpg: "", fluidBasis: "" },
    settings: { safetyMarginPpg: "", scheduleIntervalStrokes: "", wellControlStandard: "" },
    confidence: {},
    sessionContextIds: {
      projectId:     ctx?.projectId     || "",
      rigId:         ctx?.rigId         || "",
      wellId:        ctx?.wellId        || "",
      bhaId:         ctx?.bhaId         || "",
      drillStringId: ctx?.drillStringId || "",
      mudProgramId:  ctx?.mudProgramId  || "",
    },
    sourceModules: [],
  };
  for (const path of KILLSHEET_CONSUMES) {
    const r = _resolveFn(path, ctx || {});
    const value = (r.confidence === "unknown" || r.confidence === "conflict")
      ? "" : r.value;
    _assignDottedPath(snap, path, value);
    snap.confidence[path] = r.confidence;
  }
  stampEngineeringMetadata(snap, { includeSettings: true });
  return snap;
}

const _now = () => new Date().toISOString();
const _genId = () => `ks_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const _safeParse = (s, fallback) => { try { return JSON.parse(s) ?? fallback; } catch { return fallback; } };

// P0 fix — the legacy Kill Sheet Wizard used to write a completely
// different record shape into this same key (see lib/killsheet/sessionStore.js,
// now repointed to rsc.killsheets.legacy.v1). Old legacy-shaped rows may
// still be sitting in this key from before that fix; nothing has been
// migrated or deleted. A record must look like a genuine canonical Kill
// Sheet before it is ever handed to the rest of the app — this keeps a
// legacy row from ever loading as a kill sheet with silently-missing
// inputs/outputs.
function _isCanonicalKillSheetShape(rec) {
  if (!rec || typeof rec !== "object") return false;
  if (typeof rec.id !== "string" || !rec.id) return false;
  if (rec.schemaVersion !== KILLSHEET_SCHEMA_VERSION) return false;
  if (typeof rec.wellId !== "string") return false;
  if (!rec.inputs  || typeof rec.inputs  !== "object") return false;
  if (!rec.outputs || typeof rec.outputs !== "object") return false;
  if (!rec.basis   || typeof rec.basis   !== "object") return false;
  return true;
}

function _readAll() {
  const raw = _safeParse(scoped.getItem(KILLSHEET_STORAGE_KEY), []);
  return Array.isArray(raw) ? raw.filter(_isCanonicalKillSheetShape) : [];
}
function _writeAll(rows) { scoped.setItem(KILLSHEET_STORAGE_KEY, JSON.stringify(rows)); }

/**
 * Read-only diagnostic — count of rows currently stored under
 * KILLSHEET_STORAGE_KEY that fail canonical shape validation (e.g. legacy
 * wizard records saved before the key split). Never mutates storage; the
 * rows themselves are left exactly where they are.
 */
export function getUnreadableKillSheetCount() {
  const raw = _safeParse(scoped.getItem(KILLSHEET_STORAGE_KEY), []);
  if (!Array.isArray(raw)) return 0;
  return raw.filter((r) => !_isCanonicalKillSheetShape(r)).length;
}

function _validateOrThrow(rec) {
  const v = validateKillSheet(rec);
  if (!v.ok) { const e = new Error("Kill sheet failed validation."); e.validationErrors = v.errors; throw e; }
}

function _bake(rec) {
  return { ...rec, outputs: computeKillSheetOutputs(rec) };
}

export function createKillSheet(payload) {
  const base = { ...emptyKillSheet(), ...payload };
  base.id = _genId();
  base.createdAt = _now();
  base.updatedAt = base.createdAt;
  base.isActive = true;
  // Phase 1 single pipeline — when a session/context is supplied, route
  // through the SAME `_applyEngineeringContext` helper that
  // `synchronizeKillSheet` uses. First save only fills genuinely blank
  // context-mapped fields (onlyBlanks: true) — a value the operator
  // already typed on the draft wins. User edits after the first save
  // persist because updateKillSheet never touches the snapshot.
  if (payload && payload.rceContext) {
    // P0 fix — first save only fills genuinely blank context-mapped
    // fields; a value the operator already typed on the draft wins.
    _applyEngineeringContext(base, payload.rceContext, true);
    delete base.rceContext;
  }
  const baked = _bake(base);
  _validateOrThrow(baked);
  const all = _readAll();
  all.push(baked);
  _writeAll(all);
  return baked;
}

export function getKillSheet(id) {
  return _readAll().find((r) => r.id === id) || null;
}

export function listKillSheetsForWell(wellId, { includeInactive = false } = {}) {
  return _readAll()
    .filter((r) => r.wellId === wellId && (includeInactive || r.isActive !== false))
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
}

/**
 * Patch + re-validate + re-compute + persist.
 *
 * If the existing record is `locked`, only a status transition is permitted
 * (locked → superseded / archived). Any other field change throws.
 */
export function updateKillSheet(id, patch) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Kill sheet ${id} not found.`);
  const cur = all[i];

  if (cur.status === "locked") {
    const onlyStatus =
      Object.keys(patch).length === 1 && "status" in patch &&
      ["superseded", "archived"].includes(patch.status);
    if (!onlyStatus) {
      const e = new Error("This kill sheet is locked. Create a revision to change inputs.");
      e.validationErrors = [e.message];
      throw e;
    }
  }

  const merged = {
    ...cur, ...patch,
    id: cur.id, createdAt: cur.createdAt, wellId: cur.wellId,
    basis:   { ...cur.basis,   ...(patch.basis   || {}) },
    source:  { ...cur.source,  ...(patch.source  || {}) },
    inputs:  { ...cur.inputs,  ...(patch.inputs  || {}) },
  };
  merged.updatedAt = _now();
  if (patch.status === "locked" && cur.status !== "locked") {
    merged.lockedAt = _now();
    merged.lockedBy = patch.lockedBy || cur.lockedBy || "";
  }
  const baked = _bake(merged);
  _validateOrThrow(baked);
  all[i] = baked;
  _writeAll(all);
  return baked;
}

export function deleteKillSheet(id) {
  const all = _readAll();
  const next = all.filter((r) => r.id !== id);
  _writeAll(next);
  return all.length - next.length;
}

export function _clearAllKillSheets() { scoped.removeItem(KILLSHEET_STORAGE_KEY); }

// =============================================================================
// Phase 1 — Single Engineering Context pipeline
// =============================================================================
//
// Every Kill Sheet code path (createKillSheet / synchronizeKillSheet) runs
// through THIS helper. There are no other paths. The shape is:
//
//   resolve (caller-supplied ctx)  →  snapshot (build + freeze)
//                                  →  inputs / basis (apply snapshot map)
//                                  →  source lineage (record ids)
//                                  →  pullSnapshot (UI timeline)
//
// Operational inputs (sidppPsi, sicpPsi, slowPumpPressurePsi, pumpRateSpm,
// kickHeightFt, influxSizeBbl, killMethod, influxType) are NEVER touched
// here — they are not in KILLSHEET_SNAPSHOT_TO_INPUT.
//
// Pure mutator on `rec` (does not persist). Returns the mutated record.
//
// `onlyBlanks` (P0 storage-layer fix) — createKillSheet's first save must
// only fill genuinely blank context-mapped fields so a value the operator
// already typed on the draft survives; synchronizeKillSheet keeps the
// authoritative-source-always-wins behavior (onlyBlanks: false) since that
// is an explicit, user-initiated re-pull. Default false preserves prior
// behavior for any caller that doesn't pass it.
function _applyEngineeringContext(rec, ctx, onlyBlanks = false) {
  const snap = buildKillSheetContextSnapshot(ctx || {});
  rec.contextSnapshot = snap;
  _applySnapshotToInputs(rec, snap, { onlyBlanks });
  rec.source = {
    ...(rec.source || {}),
    bhaId:         snap.sessionContextIds.bhaId         || rec.source?.bhaId         || "",
    drillStringId: snap.sessionContextIds.drillStringId || rec.source?.drillStringId || "",
    rigSheetId:    snap.sessionContextIds.rigId         || rec.source?.rigSheetId    || "",
  };
  rec.pullSnapshot = {
    pulledAt: _now(),
    pulledFrom: {
      wellId:        snap.sessionContextIds.wellId        || rec.wellId,
      bhaId:         snap.sessionContextIds.bhaId         || "",
      drillStringId: snap.sessionContextIds.drillStringId || "",
      rigSheetId:    snap.sessionContextIds.rigId         || "",
    },
  };
  rec.updatedAt = _now();
  return rec;
}

/**
 * Public, non-persisting preview of the unified Engineering Context
 * pipeline. The editor's Synchronize button uses this on an UNSAVED draft
 * so the React state lands with the EXACT same `inputs` / `basis` /
 * `contextSnapshot` shape that `createKillSheet` and `synchronizeKillSheet`
 * would have produced.
 *
 * Returns a NEW record. The input record is not mutated.
 */
export function applyEngineeringContextPreview(rec, ctx) {
  const copy = {
    ...rec,
    inputs: { ...(rec.inputs || {}) },
    basis:  { ...(rec.basis  || {}) },
    source: { ...(rec.source || {}) },
  };
  // Consistent with createKillSheet (onlyBlanks: true) — the editor's
  // Synchronize-on-a-new-draft preview must match what create will do.
  return _applyEngineeringContext(copy, ctx, true);
}

/**
 * Pure helper — write every RCE-derived value from a frozen snapshot into
 * a kill-sheet record's `inputs` / `basis` blocks via the
 * `KILLSHEET_SNAPSHOT_TO_INPUT` map.
 *
 *   onlyBlanks=true  → never overwrites a non-empty user entry.
 *   onlyBlanks=false → overwrites every mapped value (used by the unified
 *                       Engineering Context pipeline — the authoritative
 *                       source always wins).
 *
 * Document-scope overrides flow through `_resolveFn`, so a permanent
 * override the user has set will be reflected in the snapshot already.
 */
function _applySnapshotToInputs(rec, snap, { onlyBlanks } = {}) {
  if (!snap || typeof snap !== "object") return rec;
  for (const [rcePath, recPath] of Object.entries(KILLSHEET_SNAPSHOT_TO_INPUT)) {
    let value = _readPathFromObject(snap, rcePath);
    if (value === "" || value == null) continue;
    // QA Fix Pack 2 — every `inputs.*` and `basis.numericThing` we write is
    // validated as `_opt` (must be a number if entered). Some publishers
    // legitimately emit non-numeric strings (e.g. `well.holeSizeIn` falling
    // back to a casing row's formatted `nominalSize` like `13-3/8"`).
    // Writing such a value would land in `rec.inputs.holeSize` and later
    // trip the `_opt` validator with "Hole size: must be a number if
    // entered." The canonical field IS `rec.inputs.holeSize`; there is no
    // shadow field. The fix is to coerce numeric-target writes to a
    // finite number, and SKIP the write when coercion fails so the
    // user's typed value (or blank) survives untouched.
    if (recPath.startsWith("inputs.")) {
      const n = Number(value);
      if (!Number.isFinite(n)) continue;   // non-numeric publisher value ⇒ do not corrupt inputs
      value = n;
    }
    // basis.fluidBasis can never truly be "blank" -- emptyKillSheet() seeds
    // it with the non-empty default "water-based" (FLUID_BASES has no ""
    // option, so validateKillSheet would reject a genuinely blank value).
    // That means the onlyBlanks guard below can never tell "still the
    // untouched default" apart from "the operator deliberately picked
    // water-based" and always skips it, so an oil-/synthetic-based Mud
    // Program never makes it into a brand-new Kill Sheet. Exempt this one
    // path from onlyBlanks -- the RCE-resolved value always wins here,
    // exactly like it already does unconditionally for a SAVED sheet's
    // Synchronize action (onlyBlanks: false).
    if (onlyBlanks && recPath !== "basis.fluidBasis") {
      const cur = _readPathFromObject(rec, recPath);
      if (cur !== "" && cur != null) continue;
    }
    _assignDottedPath(rec, recPath, value);
  }
  return rec;
}

function _readPathFromObject(obj, dotted) {
  const parts = dotted.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
}

/**
 * Synchronize Engineering Data — the ONE Kill Sheet sync action.
 *
 * Routes through `_applyEngineeringContext` with the authoritative-source-
 * always-wins behavior (onlyBlanks: false) — an explicit, user-initiated
 * re-pull is expected to overwrite every context-mapped field, unlike
 * createKillSheet's first save (onlyBlanks: true).
 *
 * Refused on approved / locked / superseded / archived sheets to preserve
 * the audit-trail invariant.
 */
export function synchronizeKillSheet(id, ctx) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Kill sheet ${id} not found.`);
  const prev = all[i];
  if (!["draft", "active"].includes(prev.status)) {
    const e = new Error(`Cannot synchronize a ${prev.status} kill sheet.`);
    e.validationErrors = [e.message];
    throw e;
  }
  const refreshed = _applyEngineeringContext({ ...prev }, ctx || {}, false);
  const baked = _bake(refreshed);
  _validateOrThrow(baked);
  all[i] = baked;
  _writeAll(all);
  return baked;
}

// =============================================================================
// Pass E — Approval workflow
// =============================================================================

/**
 * Mark a kill sheet as approved. `approver` is the human name doing the
 * approval (free text, e.g. "S. Hardman"). Approval can only flow from
 * `active`. Locked / superseded / archived sheets refuse approval.
 *
 * Approval is the gate BEFORE locking: you can lock from `approved`, but
 * `updateKillSheet({status:"locked"})` will also accept `active` for legacy
 * compatibility. The editor's UI enforces the recommended approved →
 * locked flow.
 */
export function approveKillSheet(id, { approver, notes = "" } = {}) {
  if (!approver || !String(approver).trim())
    throw new Error("Approver name is required.");
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Kill sheet ${id} not found.`);
  const cur = all[i];
  if (!["active", "draft"].includes(cur.status)) {
    const e = new Error(`Cannot approve a ${cur.status} kill sheet.`);
    e.validationErrors = [e.message];
    throw e;
  }
  const merged = {
    ...cur,
    status: "approved",
    approvedBy: String(approver).trim(),
    approvedAt: _now(),
    approvalNotes: String(notes || "").trim(),
    updatedAt: _now(),
  };
  const baked = _bake(merged);
  _validateOrThrow(baked);
  all[i] = baked;
  _writeAll(all);
  return baked;
}

// =============================================================================
// Pass E — Revision workflow
// =============================================================================

const _nextRevisionLetter = (cur) => {
  const r = String(cur || "").trim().toUpperCase();
  if (!r) return "B";        // first explicit revision: parent unlabelled → "B"
  // Single A-Z letter — bump by one. Wrap at Z → "AA" (extremely defensive).
  if (/^[A-Z]$/.test(r)) {
    return r === "Z" ? "AA" : String.fromCharCode(r.charCodeAt(0) + 1);
  }
  // Multi-letter / numeric — append a "-2", "-3", … suffix.
  const m = r.match(/^(.*?)(?:-(\d+))?$/);
  const stem = m[1] || r;
  const n = Number(m[2] || 1) + 1;
  return `${stem}-${n}`;
};

/**
 * Create a revision of a sheet (typically a locked one). Clones the
 * record with a new id, links via `parentId`, increments the revision
 * letter, resets status to `draft`. The parent flips to `superseded`.
 *
 * `reason` is optional (one of REVISION_REASONS) — controlled vocabulary.
 * `revisionNotes` is optional free text.
 */
export function createKillSheetRevision(parentId, { reason = "", notes = "" } = {}) {
  const all = _readAll();
  const parent = all.find((r) => r.id === parentId);
  if (!parent) throw new Error(`Parent kill sheet ${parentId} not found.`);
  if (parent.status === "superseded" || parent.status === "archived") {
    const e = new Error(`Cannot create a revision from a ${parent.status} sheet — start from its latest descendant.`);
    e.validationErrors = [e.message];
    throw e;
  }
  const childRecord = {
    ...parent,
    id: _genId(),
    parentId: parent.id,
    revision: _nextRevisionLetter(parent.revision),
    status: "draft",
    // Approval lineage resets on every revision.
    approvedBy: "",
    approvedAt: null,
    approvalNotes: "",
    // Lock metadata reset.
    lockedBy: "",
    lockedAt: null,
    // Revision provenance.
    revisionReason: reason || "",
    revisionNotes:  String(notes || "").trim(),
    // Surface-line displacement tracking is tied to ONE physical pumping
    // operation and its pump-counter baseline. A revision is a new sheet,
    // so it starts with no tracked operation; the parent keeps its own
    // readings/history intact (parent is retained as "superseded").
    displacement: null,
    createdAt: _now(),
    updatedAt: _now(),
  };
  const bakedChild = _bake(childRecord);
  _validateOrThrow(bakedChild);

  // Parent → superseded (preserves the audit trail; user can still view it).
  const idx = all.findIndex((r) => r.id === parentId);
  all[idx] = { ...parent, status: "superseded", updatedAt: _now() };

  all.push(bakedChild);
  _writeAll(all);
  return bakedChild;
}

/**
 * Compute a flat diff of engineering values between a child sheet and its
 * parent. Returns:
 *   { hasParent, parentRecord, changes: [{ field, label, parent, child }] }
 *
 * Only the keys we expose to the rig floor are diffed — additions and
 * removals (e.g. blank → 12.5) ARE reported. `changes` is empty when nothing
 * meaningful differs, regardless of cosmetic numeric reformatting.
 */
const _DIFF_FIELDS = [
  // Engineering Basis
  ["basis.killMethod",          "Kill Method"],
  ["basis.fluidBasis",          "Fluid Basis"],
  ["basis.influxType",          "Influx Type"],
  ["basis.wellControlStandard", "Well Control Standard"],
  // Inputs that drive the headline outputs
  ["inputs.mwInPpg",            "Mud Weight (ppg)"],
  ["inputs.sidppPsi",           "SIDPP (psi)"],
  ["inputs.sicpPsi",            "SICP (psi)"],
  ["inputs.slowPumpPressurePsi","Slow-Pump Pressure (psi)"],
  ["inputs.pumpRateSpm",        "Pump Rate (SPM)"],
  ["inputs.pumpOutputBblStk",   "Pump Output (bbl/stk)"],
  ["inputs.currentMdFt",        "Current MD (ft)"],
  ["inputs.currentTvdFt",       "Current TVD (ft)"],
  ["inputs.holeSize",           "Hole Size (in)"],
  ["inputs.casingShoeTvdFt",    "Casing Shoe TVD (ft)"],
  ["inputs.stringInternalVolBbl",  "String Internal Vol (bbl)"],
  ["inputs.bitToShoeVolBbl",       "Bit→Shoe Vol (bbl)"],
  ["inputs.shoeToSurfaceVolBbl",   "Shoe→Surface Vol (bbl)"],
  ["inputs.safetyMarginPpg",       "Safety Margin (ppg)"],
  ["inputs.scheduleIntervalStrokes","Schedule Increment (strokes)"],
  ["inputs.lotPressurePsi",        "LOT Pressure (psi)"],
  ["inputs.mwAtLotPpg",            "MW at LOT (ppg)"],
  // Derived outputs included so the diff also shows the engineering impact.
  ["outputs.kmwPpg",            "KMW (ppg)"],
  ["outputs.icpPsi",            "ICP (psi)"],
  ["outputs.fcpPsi",            "FCP (psi)"],
  ["outputs.maxAllowableMwPpg", "Max Allowable MW (ppg)"],
  ["outputs.maaspInitialPsi",   "MAASP Initial (psi)"],
  ["outputs.maaspAtKmwPsi",     "MAASP at KMW (psi)"],
];

const _readPath = (obj, dotted) => dotted.split(".").reduce(
  (acc, k) => (acc == null ? acc : acc[k]), obj);

const _normalize = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  if (Number.isFinite(n)) {
    // Round numerics to 4 decimal places for diff comparison — avoids
    // false-positives from floating-point recompute on a re-bake.
    return Math.round(n * 10000) / 10000;
  }
  return String(v).trim();
};

export function diffKillSheetFromParent(childId) {
  const all = _readAll();
  const child = all.find((r) => r.id === childId);
  if (!child) throw new Error(`Kill sheet ${childId} not found.`);
  if (!child.parentId) return { hasParent: false, parentRecord: null, changes: [] };
  const parent = all.find((r) => r.id === child.parentId);
  if (!parent) return { hasParent: false, parentRecord: null, changes: [] };

  const changes = [];
  for (const [path, label] of _DIFF_FIELDS) {
    const p = _normalize(_readPath(parent, path));
    const c = _normalize(_readPath(child,  path));
    if (p !== c) {
      changes.push({
        field: path,
        label,
        parent: _readPath(parent, path),
        child:  _readPath(child,  path),
      });
    }
  }
  return { hasParent: true, parentRecord: parent, changes };
}
